<template>
  <div class="page-container">
    <div class="content-wrap">
      <TopNavBar v-bind:secondTierLoadingDone="true"/>

      <b-container class="ga-text-page-container">
        <b-row>
          <b-col class="d-none d-md-block col-md-1 col-lg-2"></b-col>
          <b-col class="col-12 col-md-10 col-lg-8">
            <article class="ga-tool-page">
              <header class="ga-tool-page__header">
                <h1 class="ga-tool-page__title">{{ $t('Lektorat') }}</h1>
                <p class="ga-tool-page__lead">
                  {{ $t('This is a playground to test the limits of the grammar engine \'ParZu\' that is running in the background. For productive use, please download our Office Add-ins.') }}
                </p>
              </header>

              <section class="ga-tool-page__section">
                <div class="ga-translate-panels">
                  <div class="ga-translate-panel">
                    <label class="ga-form__label" for="running_text">{{ $t('Your text') }}</label>
                    <textarea
                      class="ga-translate-panel__textarea invisible-scrollbar"
                      id="running_text"
                      name="running_text"
                      v-model="running_text"
                    ></textarea>
                    <p class="ga-form__hint" v-if="!$v.running_text.required">
                      {{ $t('Running text should not empty') }}
                    </p>
                    <div class="ga-tool-page__actions">
                      <button
                        type="button"
                        class="ga-form__btn"
                        v-on:click="fixRunningText"
                      >{{ $t('Fix') }}</button>
                    </div>
                  </div>

                  <div class="ga-translate-panel">
                    <h2 class="ga-tool-page__section-title">{{ $t('gender neutral') }}</h2>
                    <div class="ga-translate-panel__output invisible-scrollbar">
                      <text-highlight
                        :queries="queries"
                        class="w-100"
                      >{{ fixedText }}</text-highlight>
                    </div>
                    <b-alert
                      v-if="tooLong"
                      show
                      variant="warning"
                      class="ga-tool-page__alert mt-2"
                      v-html="$t('Only 500 characters are processed. For longer texts please use the word add-in.')"
                    ></b-alert>
                  </div>
                </div>

                <div v-if="error" class="ga-form__error mt-2">{{ error }}</div>
                <b-alert v-if="success" show variant="success" class="ga-tool-page__alert mt-2">{{ success }}</b-alert>
              </section>
            </article>
          </b-col>
        </b-row>
      </b-container>

      <LoginWallModal
        ref="registerModal"
        :hint="$t('translate only works while logged in')"
      />
    </div>
    <GenderAppFooter/>
  </div>
</template>

<script>
import TopNavBar from '../../components/TopNavBar'
import LoginWallModal from '../../components/LoginWallModal'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import { DictionaryEntryService } from '../../services/dictionaryEntryService'
import { modalMixin } from '../../mixins/modalMixin'
import GenderAppFooter from '../../components/Footer'
import TextHighlight from 'vue-text-highlight'

export default {
  name: 'Solution',
  metaInfo () {
    return {
      title: this.title,
      meta: [
        {
          name: 'description',
          vmid: 'description',
          content: this.description
        },
        {
          property: 'og:title',
          vmid: 'og:title',
          content: this.title
        },
        {
          property: 'og:description',
          vmid: 'og:description',
          content: this.description
        },
        {
          property: 'og:url',
          vmid: 'og:url',
          content: this.canonicalUrl
        },
        {
          name: 'twitter:title',
          vmid: 'twitter:title',
          content: this.title
        },
        {
          name: 'twitter:description',
          vmid: 'twitter:description',
          content: this.description
        }
      ],
      link: [
        { rel: 'canonical', href: this.canonicalUrl }
      ]
    }
  },
  data () {
    return {
      queries: [],
      running_text: '',
      fixedText: '',
      lektoratWaiting: 0,
      lektoratWorking: 0,
      error: '',
      success: '',
      text_cache: '',
      title: 'gender app translate · gender app',
      description: 'Dies ist der experimentelle Spielplatz, auf welchem die Grammatik-Engine ihre Grenzen testet.',
      canonicalUrl: 'https://genderapp.org/translate'
    }
  },
  computed: {
    tooLong: function () {
      return this.running_text.length >= 500
    }
  },
  mixins: [validationMixin, modalMixin],
  validations: {
    running_text: {
      required
    }
  },
  methods: {
    cleanUpString (inputString, allowedCharacters = '[^a-zA-Z0-9öäüÖÄÜ\\s\\,\\.\\;\\n\\-\\?\\!\\*\\:\\/\\_]') {
      const regex = new RegExp('[^' + allowedCharacters + ']', 'g')
      return inputString.replace(regex, ' ')
    },
    sendLektorat: async function () {
      if (this.lektoratWaiting > 0) return Promise.resolve('Too many cooks')
      this.lektoratWaiting++
      await this.waitForSilence(0.1)
      this.lektoratWaiting--
      this.lektoratWorking++
      if (!this.running_text.toString().trim()) {
        this.fixedText = ''
        this.lektoratWorking--
        return Promise.resolve('empty paragraph')
      }
      await this.fixRunningText()
      this.lektoratWorking--
      return Promise.resolve('ACK')
    },
    waitForSilence: async function (secs) {
      let noOfSecsWeWait = 0.2
      if (secs) {
        noOfSecsWeWait = secs
      }
      let disturbed = true
      while (disturbed) {
        disturbed = false
        await wait(noOfSecsWeWait * 1000)
        if (this.text_cache !== this.running_text) {
          this.text_cache = this.running_text
          disturbed = true
        }
      }
      return Promise.resolve('got it')
    },
    fixRunningText: function () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.error = this.$t('Please check above error(s).')
      } else {
        if (!this.isLoggedIn()) {
          this.showRegisterModal()
          return
        }
        const cleanTruncRunningtext = this.cleanUpString(this.running_text.substring(0, 500))
        DictionaryEntryService.fixRunningText(cleanTruncRunningtext, ['N', 'V', 'ADJ', 'ART', 'PRO'], ['entries', 'fixedText'])
          .then((response) => {
            const newQueries = []
            const messageFromBackend = JSON.parse(response.data.result)
            this.fixedText = messageFromBackend.wordReplacer.fixedText
            messageFromBackend.wordReplacer.entries.forEach(entry => {
              const alternatives = entry.alternatives
              if (Array.isArray(alternatives) && alternatives.length) {
                alternatives.forEach(alternative => {
                  newQueries.push(alternative.alternative)
                })
              }
            })
            this.queries = newQueries
            this.error = ''
          })
          .catch((error) => {
            this.error = error
            this.error = ''
            this.success = ''
          })
      }
    }
  },
  watch: {
    running_text: function () {
      this.sendLektorat()
    }
  },
  mounted () {
    if (!this.isLoggedIn()) {
      this.showRegisterModal()
    }
  },
  components: { TopNavBar, GenderAppFooter, LoginWallModal, TextHighlight }
}
async function wait (ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}
</script>

<style scoped>
.invisible-scrollbar::-webkit-scrollbar {
  display: none !important;
}
</style>
